import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { ToastrService } from 'ngx-toastr';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { takeUntil, debounceTime, switchMap } from 'rxjs/operators';
import { USER_ACTIVITY_CONFIG } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserActivityService implements OnDestroy {
  private readonly INACTIVITY_TIMEOUT = USER_ACTIVITY_CONFIG.INACTIVITY_TIMEOUT;
  private readonly WARNING_TIME = USER_ACTIVITY_CONFIG.WARNING_TIME;
  
  private destroy$ = new Subject<void>();
  private inactivityTimer: any;
  private warningTimer: any;
  private isUserActive = false;
  private lastActivityTime = Date.now();
  private sessionTimeoutToast: any = null; // Store reference to session timeout warning toast

  constructor(
    private cookieService: CookieService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  /**
   * Initialize user activity monitoring
   */
  initializeActivityMonitoring(): void {
    // Check if feature is enabled
    if (!USER_ACTIVITY_CONFIG.ENABLED) {
      console.log('User activity monitoring is disabled');
      return;
    }

    // If already active, just reset the timers instead of reinitializing
    if (this.isUserActive) {
      console.log('User activity monitoring already active - resetting timers');
      this.clearInactivityTimer();
      this.clearWarningTimer();
      this.resetInactivityTimer();
      return;
    }

    console.log(`User activity monitoring started. Timeout: ${this.INACTIVITY_TIMEOUT / 1000}s, Warning: ${this.WARNING_TIME / 1000}s`);
    this.isUserActive = true;
    this.resetInactivityTimer();

    // Monitor user activity events
    const userActivityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    const activityStreams = userActivityEvents.map(event => 
      fromEvent(document, event)
    );

    // Merge all activity events and handle them
    merge(...activityStreams)
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(1000) // Debounce to avoid excessive calls
      )
      .subscribe(() => {
        this.onUserActivity();
      });

    // Monitor visibility change (tab switching)
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.onUserActivity();
        }
      });

    // Monitor focus events
    fromEvent(window, 'focus')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onUserActivity();
      });
  }

  /**
   * Handle user activity - reset timers
   */
  private onUserActivity(): void {
    if (!this.isUserActive) {
      return;
    }

    this.lastActivityTime = Date.now();
    this.resetInactivityTimer();
    this.clearWarningTimer();
    
    // Clear only the session timeout warning toast when user becomes active again
    // This prevents clearing other important toasts like inventory warnings
    if (this.sessionTimeoutToast) {
      this.toastr.clear(this.sessionTimeoutToast.toastId);
      this.sessionTimeoutToast = null;
    }
    
    console.log('User activity detected - timers reset');
  }

  /**
   * Reset the inactivity timer
   */
  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    
    this.inactivityTimer = timer(this.INACTIVITY_TIMEOUT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.showWarningAndLogout();
      });
  }

  /**
   * Show warning and schedule logout
   */
  private showWarningAndLogout(): void {
    // Clear any existing session timeout warning toasts to prevent duplicates
    if (this.sessionTimeoutToast) {
      this.toastr.clear(this.sessionTimeoutToast.toastId);
      this.sessionTimeoutToast = null;
    }
    
    // Calculate warning time in minutes and seconds for display
    const warningMinutes = Math.floor(this.WARNING_TIME / 60000);
    const warningSeconds = Math.floor((this.WARNING_TIME % 60000) / 1000);
    let warningText = '';
    
    if (warningMinutes > 0) {
      warningText = `You will be automatically signed out in ${warningMinutes} minute${warningMinutes > 1 ? 's' : ''} due to inactivity.`;
    } else {
      warningText = `You will be automatically signed out in ${warningSeconds} second${warningSeconds > 0 ? 's' : ''} due to inactivity.`;
    }
    
    console.log(`Warning shown: ${warningText}`);
    
    // Store reference to the session timeout warning toast
    this.sessionTimeoutToast = this.toastr.warning(
      warningText,
      'Session Timeout Warning',
      { timeOut: 0, extendedTimeOut: 0 }
    );

    this.warningTimer = timer(this.WARNING_TIME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('Warning timer expired - logging out user');
        this.forceLogout();
      });
  }

  /**
   * Force logout the user
   */
  private forceLogout(): void {
    this.isUserActive = false;
    
    // Clear all cookies
    this.cookieService.removeAll();
    
    // Clear timers
    this.clearInactivityTimer();
    this.clearWarningTimer();
    
    // Show logout message
    this.toastr.info('You have been signed out due to inactivity.', 'Session Expired');
    
    // Navigate to home page
    this.router.navigate(['/']);
  }

  /**
   * Clear inactivity timer
   */
  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      this.inactivityTimer.unsubscribe();
      this.inactivityTimer = null;
    }
  }

  /**
   * Clear warning timer
   */
  private clearWarningTimer(): void {
    if (this.warningTimer) {
      this.warningTimer.unsubscribe();
      this.warningTimer = null;
    }
  }

  /**
   * Stop activity monitoring
   */
  stopActivityMonitoring(): void {
    this.isUserActive = false;
    this.clearInactivityTimer();
    this.clearWarningTimer();
    // Clear only the session timeout warning toast when stopping monitoring
    if (this.sessionTimeoutToast) {
      this.toastr.clear(this.sessionTimeoutToast.toastId);
      this.sessionTimeoutToast = null;
    }
  }

  /**
   * Get remaining time before logout (in seconds)
   */
  getRemainingTime(): number {
    if (!this.isUserActive) {
      return 0;
    }
    
    const elapsed = Date.now() - this.lastActivityTime;
    const remaining = this.INACTIVITY_TIMEOUT - elapsed;
    
    return Math.max(0, Math.floor(remaining / 1000));
  }

  /**
   * Check if user is currently active
   */
  isMonitoringActive(): boolean {
    return this.isUserActive;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopActivityMonitoring();
  }
}
